// Helper functions for department and permission checks
// Backend permission format: module.feature.access (e.g. sales.leads.view)

import { canAccessDepartment, getDepartmentFromSubject, getUserMetadata, parsePermission } from './constants';
import { useAuthStore } from '@stores/auth.store';
import { useAbility } from './AbilityProvider';
import { MODULE_SIDEBAR_CONFIG, FEATURE_SIDEBAR_CONFIG, getModuleIdFromPath } from './sidebarConfig';

/**
 * Hook to check if current user can access a specific department
 */
export const useCanAccessDepartment = () => {
    const user = useAuthStore((state) => state.user);

    return (targetDepartment) => {
        if (!user) return false;
        const { department, role, adminRoles } = getUserMetadata(user);
        return canAccessDepartment(department, targetDepartment, role, adminRoles);
    };
};

/**
 * Hook to get current user's department
 */
export const useUserDepartment = () => {
    const user = useAuthStore((state) => state.user);
    if (!user) return null;
    return getUserMetadata(user).department;
};

/**
 * Hook to check if current user is admin level
 */
export const useIsAdmin = () => {
    const user = useAuthStore((state) => state.user);
    if (!user) return false;
    const { role, adminRoles } = getUserMetadata(user);
    return adminRoles.includes(role);
};

/**
 * Hook to get all user metadata
 */
export const useUserMetadata = () => {
    const user = useAuthStore((state) => state.user);
    return getUserMetadata(user);
};

/**
 * Check if the current user has a backend permission (module.feature.access).
 * Example: useCan('sales.leads.view') -> boolean
 */
export const useCan = (permissionString) => {
    const ability = useAbility();
    if (!permissionString) return false;
    const parsed = parsePermission(permissionString);
    if (!parsed) return false;
    const [action, subject] = parsed;
    return ability?.can(action, subject) ?? false;
};

/**
 * Hook that returns a function to check any backend permission string.
 * Example: const can = useCanCheck(); can('sales.leads.create')
 */
export const useCanCheck = () => {
    const ability = useAbility();
    return (permissionString) => {
        if (!permissionString || !ability) return false;
        const parsed = parsePermission(permissionString);
        if (!parsed) return false;
        const [action, subject] = parsed;
        return ability.can(action, subject);
    };
};

/**
 * Check if user has at least one permission starting with prefix (e.g. 'sales.', 'projects.').
 */
export const useHasPermissionPrefix = (prefix) => {
    const permissions = useAuthStore((state) => state.user?.permissions) || [];
    if (!prefix) return false;
    return permissions.some((p) => typeof p === 'string' && p.startsWith(prefix));
};

/**
 * Get visible module sidebar items based on CASL ability and user permissions.
 * Returns array of { id, key, icon, label } for the current scope (tenant vs owner).
 */
export const useVisibleModuleItems = (isTenantRoute = false, isOwnerRole = false) => {
    const ability = useAbility();
    const user = useAuthStore((state) => state.user);
    const permissions = user?.permissions || [];

    const can = (permissionString) => {
        if (!permissionString || !ability) return false;
        const parsed = parsePermission(permissionString);
        if (!parsed) return false;
        const [action, subject] = parsed;
        return ability.can(action, subject);
    };

    const hasPrefix = (prefix) => {
        if (!prefix) return false;
        return permissions.some((p) => typeof p === 'string' && p.startsWith(prefix));
    };

    const hasAnyPrefix = (prefixes) => {
        if (!Array.isArray(prefixes) || prefixes.length === 0) return false;
        return prefixes.some((prefix) => hasPrefix(prefix));
    };

    const hasRequiredPermission = (requiredPermission) => {
        if (!requiredPermission) return false;
        if (requiredPermission && typeof requiredPermission === 'object') {
            if (requiredPermission.operator === 'and') {
                return requiredPermission.permissions.every((perm) => can(perm));
            }
            if (requiredPermission.operator === 'or') {
                return requiredPermission.permissions.some((perm) => can(perm));
            }
        }
        if (Array.isArray(requiredPermission)) {
            return requiredPermission.some((perm) => can(perm));
        }
        return can(requiredPermission);
    };

    return MODULE_SIDEBAR_CONFIG.filter((mod) => {
        if (mod.ownerOnly && !isOwnerRole) return false;
        if (hasRequiredPermission(mod.requiredPermission)) return true;
        if (mod.permissionPrefixes && hasAnyPrefix(mod.permissionPrefixes)) return true;
        if (mod.permissionPrefix && hasPrefix(mod.permissionPrefix)) return true;
        if (mod.fallbackIfAuthenticated && user) return true;
        return false;
    }).map((mod) => {
        let defaultPath = mod.defaultPath ?? mod.path;
        let defaultPathTenant = mod.defaultPathTenant ?? mod.pathTenant;

        // Custom override for HR module default path when user lacks employee view permission
        if (mod.id === 'hr') {
            const hasEmployeesView = permissions.includes('hr.employees.view') || permissions.includes('hr.employees.view_all');
            if (!hasEmployeesView) {
                const hasAttendanceView = permissions.includes('hr.attendance.view');
                const hasLeaveView = permissions.includes('hr.leaves.view') || permissions.includes('hr.leave.view');
                if (hasAttendanceView) {
                    defaultPath = '/hr/attendance/detail';
                    defaultPathTenant = '/tenant/hr/attendance/detail';
                } else if (hasLeaveView) {
                    defaultPath = '/hr/leave';
                    defaultPathTenant = '/tenant/hr/leave';
                }
            }
        }

        return {
            id: mod.id,
            key: isTenantRoute ? defaultPathTenant : defaultPath,
            icon: mod.icon,
            label: mod.label,
        };
    });
};

/**
 * Get visible feature sidebar items for the current module (from path), based on CASL/RBAC.
 * Returns { title, features: [{ key, label, icon }] }.
 * Features with requiredPermission are gated by that permission; others are gated by module access.
 */
export const useVisibleFeatureItems = (pathname, isTenantRoute) => {
    const ability = useAbility();
    const permissions = useAuthStore((state) => state.user?.permissions) || [];

    const can = (permissionString) => {
        if (!permissionString || !ability) return false;
        const parsed = parsePermission(permissionString);
        if (!parsed) return false;
        const [action, subject] = parsed;
        return ability.can(action, subject);
    };

    const hasPrefix = (prefix) => {
        if (!prefix) return false;
        return permissions.some((p) => typeof p === 'string' && p.startsWith(prefix));
    };

    const hasAnyPrefix = (prefixes) => {
        if (!Array.isArray(prefixes) || prefixes.length === 0) return false;
        return prefixes.some((prefix) => hasPrefix(prefix));
    };

    const hasRequiredPermission = (requiredPermission) => {
        if (!requiredPermission) return false;
        if (requiredPermission && typeof requiredPermission === 'object') {
            if (requiredPermission.operator === 'and') {
                return requiredPermission.permissions.every((perm) => can(perm));
            }
            if (requiredPermission.operator === 'or') {
                return requiredPermission.permissions.some((perm) => can(perm));
            }
        }
        if (Array.isArray(requiredPermission)) {
            return requiredPermission.some((perm) => can(perm));
        }
        return can(requiredPermission);
    };

    const moduleId = getModuleIdFromPath(pathname, isTenantRoute);
    const mod = MODULE_SIDEBAR_CONFIG.find((m) => m.id === moduleId);
    const hasModuleAccess = mod
        ? (mod.requiredPermission && can(mod.requiredPermission)) ||
          (mod.permissionPrefixes && hasAnyPrefix(mod.permissionPrefixes)) ||
          (mod.permissionPrefix && hasPrefix(mod.permissionPrefix))
        : false;

    const config = FEATURE_SIDEBAR_CONFIG[moduleId];
    if (!config) return { title: 'Home', features: [] };

    const features = config.features
        .filter((f) => {
            if (f.check && !f.check(can)) return false;
            if (f.requiredPermission) return hasRequiredPermission(f.requiredPermission);
            return hasModuleAccess;
        })
        .map((f) => {
            let key = isTenantRoute && f.keyTenant != null ? f.keyTenant : f.key;

            // Custom override: If user does not have hr.employees.view or view_all,
            // change 'Attendance' feature link to target the user's personal detail screen.
            if (moduleId === 'hr' && (f.key === '/hr/attendance' || f.keyTenant === '/tenant/hr/attendance')) {
                const hasEmployeesView = permissions.includes('hr.employees.view') || permissions.includes('hr.employees.view_all');
                if (!hasEmployeesView) {
                    key = isTenantRoute ? '/tenant/hr/attendance/detail' : '/hr/attendance/detail';
                }
            }

            return {
                key,
                label: f.label,
                icon: f.icon,
            };
        });

    return { title: config.title, features };
};

/**
 * Hook to check if current user can access a specific resource/subject
 * based on department isolation
 */
export const useCanAccessResource = () => {
    const user = useAuthStore((state) => state.user);

    return (subject) => {
        if (!user) return false;

        const { department, role, adminRoles, resourceDepartments } = getUserMetadata(user);

        // Admin roles can access everything
        if (adminRoles.includes(role)) {
            return true;
        }

        const resourceDepartment = getDepartmentFromSubject(subject, resourceDepartments);

        // If resource doesn't belong to a specific department, allow access
        if (!resourceDepartment) {
            return true;
        }

        // Check department access
        return canAccessDepartment(department, resourceDepartment, role, adminRoles);
    };
};
